/**
 * SCRUM-123 — diálogos de app canónicos (AlertDialog / Dialog).
 * Reemplazo de window.confirm / window.prompt / window.alert.
 * No inventa otro sistema: envuelve el AlertDialog y Dialog del design system.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export type ConfirmDialogOptions = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Estilo destructivo en el botón de confirmar */
  destructive?: boolean;
};

export type PromptDialogOptions = {
  title: string;
  description?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  inputType?: "text" | "password" | "number";
  /** Si true, vacío + confirmar resuelve "" (no null). Default: null si vacío. */
  allowEmpty?: boolean;
};

export type AlertDialogOptions = {
  title: string;
  description?: string;
  confirmLabel?: string;
};

type AppDialogsApi = {
  confirm: (opts: ConfirmDialogOptions) => Promise<boolean>;
  prompt: (opts: PromptDialogOptions) => Promise<string | null>;
  alert: (opts: AlertDialogOptions) => Promise<void>;
};

const AppDialogsContext = createContext<AppDialogsApi | null>(null);

type ConfirmState = ConfirmDialogOptions & { open: boolean };
type PromptState = PromptDialogOptions & { open: boolean; value: string };
type AlertState = AlertDialogOptions & { open: boolean };

const DEFAULT_CONFIRM: ConfirmState = {
  open: false,
  title: "",
  description: undefined,
  confirmLabel: "Confirmar",
  cancelLabel: "Cancelar",
  destructive: false,
};

const DEFAULT_PROMPT: PromptState = {
  open: false,
  title: "",
  description: undefined,
  defaultValue: "",
  placeholder: undefined,
  confirmLabel: "Aceptar",
  cancelLabel: "Cancelar",
  inputType: "text",
  allowEmpty: false,
  value: "",
};

const DEFAULT_ALERT: AlertState = {
  open: false,
  title: "",
  description: undefined,
  confirmLabel: "Entendido",
};

export function AppDialogsProvider({ children }: { children: ReactNode }) {
  const [confirmState, setConfirmState] = useState<ConfirmState>(DEFAULT_CONFIRM);
  const [promptState, setPromptState] = useState<PromptState>(DEFAULT_PROMPT);
  const [alertState, setAlertState] = useState<AlertState>(DEFAULT_ALERT);

  const confirmResolver = useRef<((v: boolean) => void) | null>(null);
  const promptResolver = useRef<((v: string | null) => void) | null>(null);
  const alertResolver = useRef<((v: void) => void) | null>(null);

  const settleConfirm = useCallback((value: boolean) => {
    const resolve = confirmResolver.current;
    confirmResolver.current = null;
    setConfirmState(DEFAULT_CONFIRM);
    resolve?.(value);
  }, []);

  const settlePrompt = useCallback((value: string | null) => {
    const resolve = promptResolver.current;
    promptResolver.current = null;
    setPromptState(DEFAULT_PROMPT);
    resolve?.(value);
  }, []);

  const settleAlert = useCallback(() => {
    const resolve = alertResolver.current;
    alertResolver.current = null;
    setAlertState(DEFAULT_ALERT);
    resolve?.();
  }, []);

  const confirm = useCallback((opts: ConfirmDialogOptions) => {
    return new Promise<boolean>((resolve) => {
      confirmResolver.current = resolve;
      setConfirmState({
        open: true,
        title: opts.title,
        description: opts.description,
        confirmLabel: opts.confirmLabel ?? "Confirmar",
        cancelLabel: opts.cancelLabel ?? "Cancelar",
        destructive: opts.destructive ?? false,
      });
    });
  }, []);

  const prompt = useCallback((opts: PromptDialogOptions) => {
    return new Promise<string | null>((resolve) => {
      promptResolver.current = resolve;
      setPromptState({
        open: true,
        title: opts.title,
        description: opts.description,
        defaultValue: opts.defaultValue ?? "",
        placeholder: opts.placeholder,
        confirmLabel: opts.confirmLabel ?? "Aceptar",
        cancelLabel: opts.cancelLabel ?? "Cancelar",
        inputType: opts.inputType ?? "text",
        allowEmpty: opts.allowEmpty ?? false,
        value: opts.defaultValue ?? "",
      });
    });
  }, []);

  const alert = useCallback((opts: AlertDialogOptions) => {
    return new Promise<void>((resolve) => {
      alertResolver.current = resolve;
      setAlertState({
        open: true,
        title: opts.title,
        description: opts.description,
        confirmLabel: opts.confirmLabel ?? "Entendido",
      });
    });
  }, []);

  const api = useMemo<AppDialogsApi>(() => ({ confirm, prompt, alert }), [confirm, prompt, alert]);

  return (
    <AppDialogsContext.Provider value={api}>
      {children}

      <AlertDialog
        open={confirmState.open}
        onOpenChange={(open) => {
          if (!open) settleConfirm(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState.title}</AlertDialogTitle>
            {confirmState.description ? (
              <AlertDialogDescription className="whitespace-pre-line">
                {confirmState.description}
              </AlertDialogDescription>
            ) : (
              <AlertDialogDescription className="sr-only">
                Confirmá o cancelá esta acción.
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settleConfirm(false)}>
              {confirmState.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(confirmState.destructive && "bg-destructive text-destructive-foreground hover:bg-destructive/90")}
              onClick={() => settleConfirm(true)}
            >
              {confirmState.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={promptState.open}
        onOpenChange={(open) => {
          if (!open) settlePrompt(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{promptState.title}</DialogTitle>
            {promptState.description ? (
              <DialogDescription>{promptState.description}</DialogDescription>
            ) : null}
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              const trimmed = promptState.value.trim();
              if (!trimmed && !promptState.allowEmpty) {
                settlePrompt(null);
                return;
              }
              settlePrompt(promptState.value);
            }}
          >
            <Input
              autoFocus
              type={promptState.inputType}
              value={promptState.value}
              placeholder={promptState.placeholder}
              onChange={(e) => setPromptState((s) => ({ ...s, value: e.target.value }))}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => settlePrompt(null)}>
                {promptState.cancelLabel}
              </Button>
              <Button type="submit">{promptState.confirmLabel}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        onOpenChange={(open) => {
          if (!open) settleAlert();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alertState.title}</AlertDialogTitle>
            {alertState.description ? (
              <AlertDialogDescription className="whitespace-pre-line">
                {alertState.description}
              </AlertDialogDescription>
            ) : (
              <AlertDialogDescription className="sr-only">Mensaje informativo.</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => settleAlert()}>
              {alertState.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppDialogsContext.Provider>
  );
}

export function useAppDialogs(): AppDialogsApi {
  const ctx = useContext(AppDialogsContext);
  if (!ctx) {
    throw new Error("useAppDialogs debe usarse dentro de <AppDialogsProvider>");
  }
  return ctx;
}
